<template />

<script setup lang="ts">
import { computed, inject, onBeforeUnmount, useSlots } from 'vue'

import { findAndSpliceArray } from '../../utils/helpers.ts'
import { $tableKey } from '../../utils/injections.ts'
import type { SortCustomTransform, SortTransform } from '../../utils/sorting.ts'
import type { ColumnProps } from './VTableCell.vue'

export interface Props {
  numeric?: boolean
  centered?: boolean
  sortable?: boolean
  headerSelectable?: boolean
  hideOnExpand?: boolean
  wrapText?: boolean
  showOverflowTooltip?: boolean | ((row: unknown) => any)
  overflowTarget?: string
  overflowContentTarget?: string
  label?: string
  customKey?: string | number
  prop?: string
  meta?: any
  width?: number | string
  minWidth?: number | string
  maxWidth?: number | string
  visible?: boolean
  sortTransform?: SortTransform
  customSortTransform?: SortCustomTransform
  headerClass?: string
  cellClass?: string
}

export interface TableColumn {
  id: number
  props: Props & { prop: string }
  slots: ReturnType<typeof useSlots>
  classes: any
  newKey: string | number
}

const props = withDefaults(defineProps<Props>(), {
  numeric: false,
  centered: false,
  sortable: false,
  headerSelectable: false,
  hideOnExpand: false,
  wrapText: false,
  showOverflowTooltip: false,
  overflowTarget: undefined,
  overflowContentTarget: undefined,
  label: '',
  customKey: '',
  prop: '',
  meta: undefined,
  width: undefined,
  minWidth: undefined,
  maxWidth: undefined,
  visible: true,
  sortTransform: undefined,
  customSortTransform: undefined,
  headerClass: '',
  cellClass: '',
})

const table = inject($tableKey)

if (!table) {
  throw Error('VTableColumn must be a child of VTable')
}

const newKey = props.customKey || props.label

defineSlots<{
  default?: (props: ColumnProps) => any
  header?: (props: any) => any
}>()
const slots = useSlots()

const classes = computed(() => [
  props.cellClass,
  {
    'has-text-right': props.numeric && !props.centered,
    'has-text-centered': props.centered,
    'hide-on-expand': props.hideOnExpand,
    'wrap-text': props.wrapText,
  },
])

const column: TableColumn = {
  id: counter++,
  props,
  slots,
  classes,
  newKey,
}

table.tableColumns.value.push(column)

onBeforeUnmount(() => {
  findAndSpliceArray(table.tableColumns.value, (item) => item.id === column.id)
})

defineExpose({ column })
</script>

<script lang="ts">
let counter = 0
</script>
